import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://trainer-app.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const DEFAULT_SEO = {
  title: "Trainer App | Online Coaching Platform for Personal Trainers",
  description:
    "Trainer App is the all-in-one online coaching platform for personal trainers and fitness coaches. Build training programs, track client workouts, manage check-ins, and grow your online coaching business.",
};

// Per-route SEO. `canonical` consolidates duplicate paths (e.g. /about-us ->
// /about) so each marketing page is indexed as a distinct, canonical URL
// instead of all pointing at the homepage.
const ROUTE_SEO = {
  "/": {
    canonical: "/",
    title: "Trainer App | Online Coaching Platform for Personal Trainers",
    description: DEFAULT_SEO.description,
  },
  "/about": {
    canonical: "/about",
    title: "About Trainer App | Built by a Lifter, for Coaches",
    description:
      "Learn about Trainer App — a simple, no-bloat online coaching platform built by a lifter to help personal trainers program workouts, manage clients, and grow their business.",
  },
  "/about-us": {
    canonical: "/about",
    title: "About Trainer App | Built by a Lifter, for Coaches",
    description:
      "Learn about Trainer App — a simple, no-bloat online coaching platform built by a lifter to help personal trainers program workouts, manage clients, and grow their business.",
  },
  "/how-it-works": {
    canonical: "/how-it-works",
    title: "How It Works | Trainer App Online Coaching Platform",
    description:
      "See how Trainer App works: build custom training plans, track client progress, manage check-ins and payments, and coach clients online from one simple platform.",
  },
  "/pricing": {
    canonical: "/pricing",
    title: "Pricing | Trainer App Online Coaching Platform",
    description:
      "Simple, transparent pricing for personal trainers and online coaches. Start a free trial and pay as your coaching business grows with Trainer App.",
  },
  "/coaching": {
    canonical: "/coaching",
    title: "Start Your Free Trial | Trainer App for Coaches",
    description:
      "Start coaching online with Trainer App. Program workouts, track clients, collect payments, and grow your coaching business. Try it free.",
  },
  "/free-trial": {
    canonical: "/free-trial",
    title: "Free Trial | Trainer App Coaching Platform",
    description:
      "Sign up for a free trial of Trainer App and start building training programs, tracking clients, and running your online coaching business.",
  },
  "/support": {
    canonical: "/support",
    title: "Support | Trainer App",
    description:
      "Get help with Trainer App. Find answers, contact support, and learn how to get the most out of your online coaching platform.",
  },
  "/privacy-policy": {
    canonical: "/privacy-policy",
    title: "Privacy Policy | Trainer App",
    description:
      "Read the Trainer App privacy policy to understand how we collect, use, and protect your data.",
  },
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Keeps the document title, canonical link, and social meta in sync with the
 * current route. Rendered once inside the Router.
 */
const RouteSeo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = ROUTE_SEO[pathname] || {
      ...DEFAULT_SEO,
      canonical: pathname || "/",
    };
    const url = `${SITE_URL}${seo.canonical || pathname || "/"}`;

    document.title = seo.title;
    setCanonical(url);
    upsertMeta("name", "description", seo.description);

    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);
  }, [pathname]);

  return null;
};

export default RouteSeo;
